/**
 * Audit prices, Company Plan. The same bands as the website's calculators
 * (Ryoma-Website src/assets/js/tools.mjs, BANDS); change both together.
 * The fee actually agreed is entered on each audit; this only suggests it.
 */
export interface PriceBand {
  min: number;
  max: number;
  label: string;
  feePence: number;
  /** True when the fee is quoted case by case and this is only a starting figure */
  quoted: boolean;
}

export const PRICE_BANDS: PriceBand[] = [
  { min: 0, max: 299, label: "Under 300 orders a month: free shipping guide", feePence: 0, quoted: false },
  { min: 300, max: 999, label: "300 to 999 orders a month: Shipping Review", feePence: 29500, quoted: false },
  { min: 1000, max: 2999, label: "1,000 to 2,999 orders a month", feePence: 115000, quoted: false },
  { min: 3000, max: 7999, label: "3,000 to 7,999 orders a month", feePence: 225000, quoted: false },
  { min: 8000, max: 19999, label: "8,000 to 19,999 orders a month", feePence: 300000, quoted: false },
  { min: 20000, max: Infinity, label: "20,000 or more orders a month: quoted, from £3,500", feePence: 350000, quoted: true },
];

export function bandFor(ordersPerMonth: number): PriceBand {
  const n = Math.round(ordersPerMonth);
  return PRICE_BANDS.find((b) => n >= b.min && n <= b.max) ?? PRICE_BANDS[PRICE_BANDS.length - 1]!;
}

/** Saving in year one after the fee, never below zero (as on the website). */
export function afterFee(annualPence: number, feePence: number): number {
  return Math.max(0, annualPence - feePence);
}

/** Months for the saving to cover the fee, to one decimal place; null when there is no saving. */
export function paybackMonths(annualPence: number, feePence: number): number | null {
  if (annualPence <= 0) return null;
  if (feePence <= 0) return 0;
  return Math.round((feePence / (annualPence / 12)) * 10) / 10;
}

/** Implementation is priced at about 18% of first-year savings (Company Plan), to the nearest £50. */
export const IMPLEMENTATION_SHARE = 0.18;
/** The monthly retainer is 20% of the monthly saving (Company Plan). */
export const RETAINER_CAP_SHARE = 0.2;

const toNearest = (pence: number, step: number) => Math.round(pence / step) * step;

/** Suggested one-off implementation fee, rounded to the nearest £50. Zero when there is no saving. */
export function suggestedImplementationFee(annualSavingPence: number): number {
  if (annualSavingPence <= 0) return 0;
  return toNearest(annualSavingPence * IMPLEMENTATION_SHARE, 5000);
}

/** Suggested monthly retainer: 20% of the monthly saving, to the nearest £25 (Company Plan). */
export function retainerCap(annualSavingPence: number): number {
  if (annualSavingPence <= 0) return 0;
  return toNearest((annualSavingPence * RETAINER_CAP_SHARE) / 12, 2500);
}
